/// Main AI chat panel with message list, input, model picker, and quick actions.
///
/// Integrates into the BottomPanel as the "AI Chat" tab. Uses useAIChat hook
/// for all AI interactions, auto-scrolls on new messages, and renders
/// AISettingsDialog so it can be triggered from the input area.

import { useState, useEffect, useRef, useCallback } from "react";
import * as ScrollArea from "@radix-ui/react-scroll-area";
import { Sparkles, HelpCircle } from "lucide-react";
import { useAIChat } from "@/hooks/useAIChat";
import { useAIStore } from "@/stores/aiStore";
import { useEditorStore } from "@/stores/editorStore";
import { AIChatMessage } from "@/components/ai/AIChatMessage";
import { AIChatInput } from "@/components/ai/AIChatInput";
import { AISettingsDialog } from "@/components/ai/AISettingsDialog";
import { AIGeneratedDiagram, extractDiagramContent } from "@/components/ai/AIGeneratedDiagram";
import { AIErrorDiagnosis } from "@/components/ai/AIErrorDiagnosis";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AIChatPanel() {
  const {
    sendMessage,
    abortResponse,
    explainElement,
    generateBPMN,
    generateNodeFlow,
    isStreaming,
  } = useAIChat();

  const activeTabId = useEditorStore((s) => s.activeTabId);
  const conversations = useAIStore((s) => s.conversations);
  const streamingTabId = useAIStore((s) => s.streamingTabId);
  const selection = useEditorStore((s) => {
    const tabId = s.activeTabId;
    if (!tabId) return null;
    return s.editorStates.get(tabId)?.selection ?? null;
  });

  const hasSelection = selection !== null && selection.selectedIds.length > 0;

  // Generation mode: when set, the next send calls the appropriate generator
  const [generationMode, setGenerationMode] = useState<"bpmn" | "nodeflow" | null>(null);

  // Get messages for the current tab
  const tabId = activeTabId ?? "default";
  const conversation = conversations.get(tabId);
  const messages = conversation?.messages ?? [];

  // Auto-scroll ref
  const scrollEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  // Handlers
  const handleSend = useCallback(
    (content: string) => {
      if (generationMode === "bpmn") {
        generateBPMN(content);
        setGenerationMode(null);
      } else if (generationMode === "nodeflow") {
        generateNodeFlow(content);
        setGenerationMode(null);
      } else {
        sendMessage(content);
      }
    },
    [sendMessage, generateBPMN, generateNodeFlow, generationMode],
  );

  const handleRetry = useCallback(
    (messageContent: string) => {
      // Retry by re-sending the last user message
      sendMessage(messageContent);
    },
    [sendMessage],
  );

  const handleGenerateBPMN = useCallback(() => {
    setGenerationMode("bpmn");
  }, []);

  const handleGenerateNodeFlow = useCallback(() => {
    setGenerationMode("nodeflow");
  }, []);

  const handleExplainSelection = useCallback(() => {
    explainElement();
  }, [explainElement]);

  // Find the last user message for retry purposes
  const findLastUserMessage = useCallback((): string => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === "user") return messages[i].content;
    }
    return "";
  }, [messages]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div data-testid="ai-chat-panel" className="flex flex-col h-full">
      {/* Message list */}
      <ScrollArea.Root className="flex-1 min-h-0">
        <ScrollArea.Viewport className="h-full w-full px-3 py-3">
          {messages.length === 0 ? (
            /* Empty state */
            <div className="flex flex-col items-center justify-center h-full gap-4 py-6">
              <p className="text-sm text-zinc-500 text-center max-w-xs">
                Ask InovaBPM AI to help design processes, explain elements, or diagnose errors.
              </p>

              {/* Quick action buttons */}
              <div className="flex flex-wrap gap-2 justify-center">
                <QuickAction
                  icon={<Sparkles size={14} />}
                  label="Generate BPMN Diagram"
                  onClick={handleGenerateBPMN}
                />
                <QuickAction
                  icon={<Sparkles size={14} />}
                  label="Generate Node Flow"
                  onClick={handleGenerateNodeFlow}
                />
                <QuickAction
                  icon={<HelpCircle size={14} />}
                  label="Explain Selection"
                  onClick={handleExplainSelection}
                  disabled={!hasSelection}
                />
              </div>
            </div>
          ) : (
            /* Message list */
            <div>
              {messages.map((msg, idx) => {
                // Skip system messages from auto-diagnosis
                if (msg.role === "system") return null;

                const isLastAssistant =
                  msg.role === "assistant" &&
                  idx === messages.length - 1 &&
                  streamingTabId === tabId;

                // Check if this assistant message contains a generated diagram
                const hasBPMNContent =
                  msg.role === "assistant" &&
                  !msg.isError &&
                  !isLastAssistant &&
                  extractDiagramContent(msg.content, "bpmn") !== null;

                const hasNodeFlowContent =
                  msg.role === "assistant" &&
                  !msg.isError &&
                  !isLastAssistant &&
                  !hasBPMNContent &&
                  extractDiagramContent(msg.content, "nodeflow") !== null;

                // Check if this is a diagnosis message
                const isDiagnosisMsg =
                  msg.role === "assistant" && msg.isDiagnosis === true && !isLastAssistant;

                const diagramType = hasBPMNContent
                  ? "bpmn"
                  : hasNodeFlowContent
                    ? "nodeflow"
                    : null;
                const extractedContent = diagramType
                  ? extractDiagramContent(msg.content, diagramType)
                  : null;

                return (
                  <div key={`${msg.timestamp}-${idx}`}>
                    <AIChatMessage
                      message={msg}
                      isStreaming={isLastAssistant && isStreaming}
                      onRetry={msg.isError ? () => handleRetry(findLastUserMessage()) : undefined}
                    />
                    {extractedContent && diagramType && (
                      <AIGeneratedDiagram
                        content={extractedContent}
                        type={diagramType}
                        rawResponse={msg.content}
                      />
                    )}
                    {isDiagnosisMsg && msg.diagnosisTabId && (
                      <AIErrorDiagnosis
                        diagnosis={msg.content}
                        diagramType={
                          (useEditorStore.getState().tabs.find((t) => t.id === msg.diagnosisTabId)
                            ?.editorType as "bpmn" | "nodeflow") ?? "bpmn"
                        }
                        tabId={msg.diagnosisTabId}
                      />
                    )}
                  </div>
                );
              })}
              <div ref={scrollEndRef} />
            </div>
          )}
        </ScrollArea.Viewport>
        <ScrollArea.Scrollbar
          orientation="vertical"
          className="flex w-2 touch-none select-none rounded-full bg-zinc-800 p-0.5"
        >
          <ScrollArea.Thumb className="relative flex-1 rounded-full bg-zinc-600" />
        </ScrollArea.Scrollbar>
      </ScrollArea.Root>

      {/* Generation mode indicator */}
      {generationMode && (
        <div className="flex items-center justify-between px-3 py-1.5 bg-blue-600/10 border-t border-blue-500/20">
          <span className="text-xs text-blue-400">
            {generationMode === "bpmn"
              ? "Describe your BPMN process..."
              : "Describe your data transformation..."}
          </span>
          <button
            type="button"
            onClick={() => setGenerationMode(null)}
            className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Chat input */}
      <AIChatInput onSend={handleSend} onAbort={abortResponse} isStreaming={isStreaming} />

      {/* Settings dialog (rendered here so it can be triggered from input) */}
      <AISettingsDialog />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Quick action button
// ---------------------------------------------------------------------------

function QuickAction({
  icon,
  label,
  onClick,
  disabled,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex items-center gap-1.5 rounded-lg border border-zinc-700 bg-zinc-800/50 px-3 py-1.5 text-xs text-zinc-400 hover:text-zinc-200 hover:border-zinc-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
    >
      {icon}
      {label}
    </button>
  );
}
